import * as THREE from 'three/webgpu';
import type {Subscription} from 'rxjs';
import type {Viewer} from '../../viewer/Viewer';
import {ViewerMode} from '../../viewer/ViewerMode';
import {
  type FeatureFace,
  type FeatureLine,
  type FeaturePoint,
  isFeatureFace,
  isFeatureLine,
  isFeaturePoint,
} from '../../mesh/feature';
import {type DatumAxis, isDatumAxis} from '../DatumAxis';
import {
  buildDatumReferenceEntityId,
  DatumReferenceEntityKind,
  parseDatumReferenceEntityId
} from '../DatumReferenceEntityId';
import {type DatumPlane, getDatumPlaneIdFromObject, isDatumPlaneMesh, isDatumPlaneObject} from '../DatumPlane';
import {type DatumPoint, isDatumPoint} from '../DatumPoint';
import {ReferenceCreationContext} from '../ReferenceCreationContext';
import {
  type PatternDirectionArrowAxisDragEvent,
  PatternDirectionArrowAxisDragPhase,
} from '../../manager/directionArrow/PatternDirectionArrowAxisDragTypes';
import {
  resolveImportedAssemblyTopologySelectionRef
} from '../../imported-model/selection/resolveImportedAssemblyTopologySelectionRef';
import {resolveTopologySelectionPick} from '../../utils/feature/resolveTopologySelectionPick';
import {resolveModelTopologyProxyObject} from '../../utils/feature/resolveModelTopologyOwnerResultMesh';
import {convertMmToM} from '../../utils/unit';
import {resolveAssemblyTopologyReferenceObject} from '../shared/resolveAssemblyTopologyReferenceObject';
import {
  DEFAULT_PLANE_TOLERANCE,
  PlaneConstraintType,
  PlaneProjectionType,
  PlaneReferenceRole,
  PlaneSolveStatus,
  type PlaneTolerancePolicy,
  ReferenceCommandLifecycle,
  ReferenceCommandModeSource,
  ReferenceEntitySemanticType,
} from '../ReferenceFeatureTypes';
import {PlaneCommandRuleEngine} from './PlaneCommandRuleEngine';
import {
  type PlaneCommandControllerOptions,
  type PlaneCommandState,
  type PlaneCommandViewModel,
  type PlanePreviewState,
  type PlaneSelectionCandidate,
  type PlaneSelectionReferencePayload,
  PlaneSlotKey,
} from './PlaneCommandTypes';
import {
  REFERENCE_PLANE_CALCULATION_SOURCE_VERSION,
  type ReferencePlaneCalculationReference,
  ReferencePlaneCalculationReferenceBindingKind,
  type ReferencePlaneCalculationSource,
} from './ReferencePlaneCalculationSource';
import type {DatumPlaneHistorySnapshot} from '../DatumPlaneHistoryTypes';
import type {
  PlaneEntityReference,
  PlaneFeatureDefinition,
  PlaneFeatureReference,
  PlaneFeatureResult,
  PlaneResolvedEntity,
} from './ReferencePlaneSolver';
import {ReferencePlaneSolver} from './ReferencePlaneSolver';
import {type DatumPlaneDisplaySize, resolveDefaultDatumPlaneDisplaySize,} from './ReferencePlaneDisplaySizePolicy';

export type {
  PlaneSelectionCandidate,
  PlaneCommandViewModel,
};

/**
 * 基准面可接收的选择对象。
 */
type PlaneSelectableObject = FeatureFace | FeatureLine | FeaturePoint | DatumAxis | DatumPoint | THREE.Object3D;

/**
 * 判断当前特征面是否为非平面曲面。
 *
 * @param object 待判断对象。
 * @returns 若为非平面曲面返回 `true`。
 */
function isNonPlanarFeatureSurfaceObject(object: FeatureFace): boolean {
  const isFeatureSurface = Reflect.get(object, 'isFeatureSurface') === true;
  const surfaceKind = Reflect.get(object, 'surfaceKind');
  return isFeatureSurface && surfaceKind !== 'plane';
}


/**
 * 判断语义类型是否为平面类。
 *
 * @param kind 语义类型。
 * @returns 若为平面类返回 `true`。
 */
function isPlaneLikeKind(kind: ReferenceEntitySemanticType): boolean {
  return kind === ReferenceEntitySemanticType.DatumPlane || kind === ReferenceEntitySemanticType.FeatureFace;
}

/**
 * 创建基准面特征 ID。
 *
 * @returns 唯一特征 ID。
 */
function createPlaneFeatureId(): string {
  return `datum-plane:${Date.now()}:${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * 创建默认命令状态。
 *
 * @returns 默认命令状态。
 */
function createDefaultStateInternal(): PlaneCommandState {
  return {
    lifecycle: ReferenceCommandLifecycle.Closed,
    modeSource: ReferenceCommandModeSource.AutoInferred,
    activeSlot: PlaneSlotKey.First,
    refs: {
      [PlaneSlotKey.First]: null,
      [PlaneSlotKey.Second]: null,
      [PlaneSlotKey.Third]: null,
    },
    constraint: PlaneConstraintType.Auto,
    projectionType: PlaneProjectionType.NearestOnSurface,
    distanceOrAngle: 0,
    reverseNormal: false,
    reverseOffset: false,
    originOnCurve: false,
    planeCount: 1,
    solutionIndex: 0,
    solveStatus: PlaneSolveStatus.UnderDefined,
    preview: { ok: false },
    dirty: false,
  };
}

/**
 * 根据槽位解析引用角色。
 *
 * @param slot 槽位键值。
 * @returns 角色枚举值。
 */
function roleFromSlot(slot: PlaneSlotKey): PlaneReferenceRole {
  if (slot === PlaneSlotKey.First) return PlaneReferenceRole.First;
  if (slot === PlaneSlotKey.Second) return PlaneReferenceRole.Second;
  return PlaneReferenceRole.Third;
}


/**
 * 根据引用角色解析命令槽位。
 *
 * @param role 引用角色。
 * @returns 命令槽位。
 */
function slotFromRole(role: PlaneReferenceRole): PlaneSlotKey {
  if (role === PlaneReferenceRole.First) return PlaneSlotKey.First;
  if (role === PlaneReferenceRole.Second) return PlaneSlotKey.Second;
  return PlaneSlotKey.Third;
}

/**
 * 从特征定义引用中读取布尔标记。
 *
 * @param refs 特征定义引用集合。
 * @param key 标记键。
 * @returns 任一引用开启该标记时返回 `true`。
 */
function resolveFeatureReferenceFlag(
  refs: PlaneFeatureReference[],
  key: keyof PlaneFeatureReference['flags'],
): boolean {
  return refs.some((ref) => ref.flags?.[key] === true);
}


/**
 * 根据选择候选推断实体种类。
 *
 * @param candidate 选择候选。
 * @returns 基准面实体种类。
 */
function entityKindFromCandidate(candidate: PlaneSelectionCandidate): ReferenceEntitySemanticType {
  return candidate.entityType;
}

/**
 * 从命令状态构建几何引用。
 *
 * @param state 命令状态。
 * @returns 引用列表。
 */
function buildGeometryReferences(state: PlaneCommandState): PlaneEntityReference[] {
  const list: PlaneEntityReference[] = [];
  const slots = [PlaneSlotKey.First, PlaneSlotKey.Second, PlaneSlotKey.Third];
  slots.forEach((slot) => {
    const candidate = state.refs[slot];
    if (!candidate) {
      return;
    }
    // 只收集已占用槽位，保证求解器接收的引用集合连续且稳定。
    list.push({
      entityId: candidate.entityId,
      entityType: candidate.entityType,
      slot,
      topologyPick: candidate.payload?.topologyPick ?? null,
      assemblyTopologyRef: candidate.payload?.assemblyTopologyRef ?? null,
    });
  });
  return list;
}

/**
 * 根据命令状态构建特征定义。
 *
 * @param featureId 特征 ID。
 * @param state 命令状态。
 * @returns 可序列化的特征定义。
 */
function buildDefinitionInternal(featureId: string, state: PlaneCommandState): PlaneFeatureDefinition {
  const slots = [PlaneSlotKey.First, PlaneSlotKey.Second, PlaneSlotKey.Third];
  const refs: PlaneFeatureReference[] = slots
    .map((slot): PlaneFeatureReference | null => {
      const candidate = state.refs[slot];
      if (!candidate) {
        return null;
      }
      // 距离与角度约束才需要读取标量值，其余约束保持 `null` 避免脏数据残留。
      const effectiveScalar = (
        state.constraint === PlaneConstraintType.Distance
        || state.constraint === PlaneConstraintType.Angle
      ) ? state.distanceOrAngle : null;
      return {
        role: roleFromSlot(slot),
        entityRef: {
          entityId: candidate.entityId,
          entityType: candidate.entityType,
          slot,
          topologyPick: candidate.payload?.topologyPick ?? null,
          assemblyTopologyRef: candidate.payload?.assemblyTopologyRef ?? null,
        },
        entityKind: entityKindFromCandidate(candidate),
        constraint: state.constraint,
        scalar: effectiveScalar,
        flags: {
          reverseNormal: state.reverseNormal,
          reverseOffset: state.reverseOffset,
          originOnCurve: state.originOnCurve,
        },
      };
    })
    .filter((item): item is PlaneFeatureReference => Boolean(item));

  return {
    id: featureId,
    refs: refs,
    projectionType: state.projectionType,
    solutionIndex: state.solutionIndex,
    planeCount: Math.max(1, Math.floor(state.planeCount)),
    solveStatus: state.solveStatus,
    version: 1,
  };
}


/**
 * 判断候选是否为基准几何引用。
 *
 * @param candidate 选择候选。
 * @returns 若候选来自基准几何则返回 `true`。
 */
function isDatumReferenceCandidate(candidate: PlaneSelectionCandidate): boolean {
  return (
    candidate.entityType === ReferenceEntitySemanticType.DatumPlane
    || candidate.entityType === ReferenceEntitySemanticType.DatumAxis
    || candidate.entityType === ReferenceEntitySemanticType.DatumPoint
  );
}

/**
 * 解析选择候选的计算绑定类型。
 *
 * @param candidate 选择候选。
 * @returns 计算绑定类型。
 */
function resolveCalculationReferenceBindingKind(
  candidate: PlaneSelectionCandidate,
): ReferencePlaneCalculationReferenceBindingKind {
  if (candidate.payload?.assemblyTopologyRef) {
    return ReferencePlaneCalculationReferenceBindingKind.AssemblyTopology;
  }
  if (isDatumReferenceCandidate(candidate)) {
    return ReferencePlaneCalculationReferenceBindingKind.DatumReference;
  }
  return ReferencePlaneCalculationReferenceBindingKind.RuntimeObject;
}

/**
 * 从场景对象构建选择附加载荷。
 *
 * @param object 当前选择对象。
 * @returns 选择附加载荷。
 */
function buildSelectionReferencePayload(object: THREE.Object3D): PlaneSelectionReferencePayload {
  const topologyPick = resolveTopologySelectionPick(object);
  const assemblyTopologyRef = resolveImportedAssemblyTopologySelectionRef(object, topologyPick);
  return {
    sourceObjectUuid: object.uuid,
    sourceObjectName: object.name,
    topologyPick,
    assemblyTopologyRef,
  };
}

/**
 * 根据命令状态构建计算引用快照。
 *
 * @param state 命令状态。
 * @returns 计算引用快照。
 */
function buildCalculationReferencesInternal(state: PlaneCommandState): ReferencePlaneCalculationReference[] {
  const slots = [PlaneSlotKey.First, PlaneSlotKey.Second, PlaneSlotKey.Third];
  return slots
    .map((slot): ReferencePlaneCalculationReference | null => {
      const candidate = state.refs[slot];
      if (!candidate) {
        return null;
      }
      return {
        role: roleFromSlot(slot),
        entityId: candidate.entityId,
        entityType: candidate.entityType,
        label: candidate.label,
        bindingKind: resolveCalculationReferenceBindingKind(candidate),
        sourceObjectUuid: candidate.payload?.sourceObjectUuid,
        sourceObjectName: candidate.payload?.sourceObjectName,
        topologyPick: candidate.payload?.topologyPick ?? null,
        assemblyTopologyRef: candidate.payload?.assemblyTopologyRef ?? null,
      };
    })
    .filter((item): item is ReferencePlaneCalculationReference => Boolean(item));
}

/**
 * 构建正式基准面的计算来源快照。
 *
 * @param input 构建输入。
 * @returns 计算来源快照。
 */
function buildCalculationSourceInternal(input: {
  featureId: string;
  state: PlaneCommandState;
  definition: PlaneFeatureDefinition;
  creationContext: ReferenceCreationContext;
  result: PlaneFeatureResult;
  generatedPlaneIndex: number;
  generatedPlaneCount: number;
}): ReferencePlaneCalculationSource {
  return {
    schemaVersion: REFERENCE_PLANE_CALCULATION_SOURCE_VERSION,
    featureId: input.featureId,
    creationContext: input.creationContext,
    definition: input.definition,
    references: buildCalculationReferencesInternal(input.state),
    selectedSolutionIndex: input.result.activeSolutionIndex ?? input.state.solutionIndex,
    generatedPlaneIndex: input.generatedPlaneIndex,
    generatedPlaneCount: input.generatedPlaneCount,
  };
}


/**
 * 收集当前已选引用的分类序列。
 *
 * @param state 命令状态。
 * @returns 按槽位排序的选择分类。
 */
function collectSelectionKindsInternal(state: PlaneCommandState): ReferenceEntitySemanticType[] {
  const ordered = [PlaneSlotKey.First, PlaneSlotKey.Second, PlaneSlotKey.Third]
    .map((slot) => state.refs[slot])
    .filter((item): item is PlaneSelectionCandidate => Boolean(item));
  return ordered.map((item) => item.kind);
}

/**
 * 根据当前状态推断约束。
 *
 * @param state 命令状态。
 * @returns 推断后的约束类型。
 */
function inferConstraintForStateInternal(state: PlaneCommandState): PlaneConstraintType {
  return PlaneCommandRuleEngine.inferConstraint(collectSelectionKindsInternal(state));
}

/**
 * 获取当前状态下可用约束集合。
 *
 * @param state 命令状态。
 * @returns 可用约束列表。
 */
function availableConstraintsForStateInternal(state: PlaneCommandState): PlaneConstraintType[] {
  return PlaneCommandRuleEngine.resolveAvailableConstraints(collectSelectionKindsInternal(state));
}

/**
 * 根据 Viewer 模式解析正式参考对象创建来源。
 *
 * @param mode 当前 Viewer 模式。
 * @returns 创建来源上下文。
 */
function resolveReferenceCreationContextFromViewerMode(mode: ViewerMode): ReferenceCreationContext {
  return (mode === ViewerMode.Assembly || mode === ViewerMode.MoveComponent)
    ? ReferenceCreationContext.Assembly
    : ReferenceCreationContext.PartFeature;
}


/**
 * 根据正式参考对象创建来源解析命令结束后应回落到的 Viewer 模式。
 *
 * @param context 正式对象创建来源。
 * @returns 命令结束后应恢复的 Viewer 模式。
 */
function resolveReferenceCommandExitViewerMode(context: ReferenceCreationContext): ViewerMode {
  return context === ReferenceCreationContext.Assembly
    ? ViewerMode.Assembly
    : ViewerMode.Default;
}


/**
 * 判断当前是否处于“单曲面待相切”状态。
 *
 * @param state 命令状态。
 * @returns 若仅选中一个曲面且当前约束为相切，返回 `true`。
 */
function isSingleSurfaceTangentPendingState(state: PlaneCommandState): boolean {
  const kinds = collectSelectionKindsInternal(state);
  return (
    kinds.length === 1
    && kinds[0] === ReferenceEntitySemanticType.FeatureSurface
    && state.constraint === PlaneConstraintType.Tangent
  );
}

/**
 * 基准面无效参考组合错误文案。
 */
const INVALID_REFERENCE_COMBINATION_MESSAGE = '当前参考引用和约束的组合无效';


/**
 * 基准面编辑特征会话。
 */
interface ReferencePlaneEditSession {
  /** 正在编辑的基准面 ID。 */
  planeId: number;
  /** 原基准面特征 ID。 */
  featureId: string;
  /** 多输出基准面中的原输出索引。 */
  generatedPlaneIndex: number;
  /** 编辑打开时恢复的初始约束。 */
  initialConstraint: PlaneConstraintType;
  /** 进入编辑前原基准面的可见性。 */
  previousVisible: boolean;
}



/** 基准面预览法线箭头最小参考尺寸。 */
const REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_MIN_SIZE = 0.1;

/** 基准面预览法线箭头渲染层级。 */
const REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_RENDER_ORDER = 991;

/** 基准面预览法线箭头槽位前缀。 */
const REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_SLOT_PREFIX = '__reference_plane_preview_normal_arrow__:';

/** 基准面预览法线箭头拖拽上下文。 */
const REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_DRAG_CONTEXT = 'reference-plane-preview-normal-arrow';

/** 基准面预览法线箭头杆身半径像素尺寸。 */
const REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_SHAFT_RADIUS_PIXELS = 2;

/** 基准面预览法线箭头杆身长度像素尺寸。 */
const REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_SHAFT_LENGTH_PIXELS = 46;

/** 基准面预览法线箭头锥头半径像素尺寸。 */
const REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_HEAD_RADIUS_PIXELS = 8;

/** 基准面预览法线箭头锥头长度像素尺寸。 */
const REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_HEAD_LENGTH_PIXELS = 18;

/** 基准面预览法线箭头锚点下方保留像素尺寸。 */
const REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_BOTTOM_LENGTH_PIXELS = 3;

/** 基准面预览法线箭头锚点上方杆身像素尺寸。 */
const REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_TOP_LENGTH_PIXELS = 43;

/** 基准面预览法线箭头锚点抬升像素尺寸。 */
const REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_ANCHOR_LIFT_PIXELS = 4;

/**
 * 基准面预览法线箭头尺寸。
 */
interface ReferencePlanePreviewNormalArrowSize {
  /** 杆身半径。 */
  shaftRadius: number;
  /** 杆身长度。 */
  shaftLength: number;
  /** 箭头锥头半径。 */
  headRadius: number;
  /** 箭头锥头长度。 */
  headLength: number;
  /** 锚点下方保留长度。 */
  bottomLength: number;
  /** 锚点上方法线指示长度。 */
  topLength: number;
  /** 锚点沿法线方向抬升距离。 */
  anchorLift: number;
}

/**
 * 解析基准面预览法线箭头尺寸。
 *
 * @param width 预览基准面显示宽度。
 * @param height 预览基准面显示高度。
 * @returns 法线箭头尺寸。
 */
function resolveReferencePlanePreviewNormalArrowSize(
  width: number,
  height: number,
): ReferencePlanePreviewNormalArrowSize {
  // 以预览面片短边作为箭头基准，保证窄长基准面不会产生过长法线箭头。
  const referenceSize = Math.max(
    Math.min(Math.abs(width), Math.abs(height)),
    REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_MIN_SIZE,
  );

  // 箭头整体按基准面显示尺寸缩放，保持不同尺寸预览面片下的视觉比例一致。
  const headLength = Math.max(referenceSize * 0.08, referenceSize * 0.02);
  const shaftLength = Math.max(referenceSize * 0.16, headLength * 1.8);
  const shaftRadius = Math.max(referenceSize * 0.006, 0.001);
  const headRadius = Math.max(referenceSize * 0.022, shaftRadius * 1.8);

  // 底部只保留短尾巴，箭头主体朝法线方向伸出，避免穿过预览面片中心造成遮挡。
  const bottomLength = Math.max(shaftLength * 0.04, shaftRadius * 0.4);
  const topLength = Math.max(shaftLength - bottomLength, headLength * 1.2);
  const anchorLift = Math.max(bottomLength + shaftRadius * 0.3, headLength * 0.08);

  // 返回完整尺寸包，让渲染阶段只负责实例化箭头，不重复散落比例计算。
  return {
    shaftRadius,
    shaftLength,
    headRadius,
    headLength,
    bottomLength,
    topLength,
    anchorLift,
  };
}



/**
 * 解析预览基准面的显示尺寸。
 *
 * @param value 求解器给出的显示尺寸。
 * @param fallback 未提供显示尺寸时使用的默认尺寸。
 * @returns 可传入 `PlaneGeometry` 的显示尺寸。
 */
function resolvePreviewDatumPlaneDisplaySize(
  width: number | undefined,
  height: number | undefined,
  fallback: number,
): DatumPlaneDisplaySize {
  const fallbackSize = resolveDefaultDatumPlaneDisplaySize(fallback);
  return {
    width: Number.isFinite(width) && Number(width) > 0 ? Number(width) : fallbackSize.width,
    height: Number.isFinite(height) && Number(height) > 0 ? Number(height) : fallbackSize.height,
  };
}

/**
 * 解析正式基准面的显式显示尺寸。
 *
 * @param value 求解器给出的显示尺寸。
 * @returns 有效尺寸；未提供时返回 `undefined` 以沿用基准面默认尺寸。
 */
function resolveCreatedDatumPlaneDisplaySize(value: number | undefined): number | undefined {
  if (!Number.isFinite(value)) {
    return undefined;
  }
  return Number(value);
}


/**
 * 基准面统一控制器。
 *
 * 设计说明：
 * 1. 直接承接命令状态、预览渲染、正式创建全链路；
 * 2. 原 `PlaneCommandController.ts` 的核心状态与行为已收口到本类，不再依赖独立控制器实现；
 * 3. 本文件仅保留一个公开 class。
 */
export class ReferencePlaneController {
  private readonly viewer: Viewer;
  private readonly commandOptions: PlaneCommandControllerOptions;
  private readonly solver: ReferencePlaneSolver;
  private readonly tolerance: PlaneTolerancePolicy;
  private state: PlaneCommandState = createDefaultStateInternal();
  /** 当前命令会话的正式对象创建来源。 */
  private pendingCreationContext: ReferenceCreationContext = ReferenceCreationContext.PartFeature;

    /** 当前基准面编辑特征会话；为空表示创建模式。 */
  private editSession: ReferencePlaneEditSession | null = null;

    private previewGroup: THREE.Group | null = null;


    /** 当前基准面预览法线箭头槽位集合。 */
  private readonly previewNormalArrowSlots = new Set<string>();

  /** 基准面预览法线箭头拖拽订阅。 */
  private previewNormalArrowDragSubscription: Subscription | null = null;


    /**
   * 创建基准面控制器。
   *
   * @param viewer 视图实例。
   * @param commandOptions 控制器配置。
   */
  public constructor(viewer: Viewer, commandOptions: PlaneCommandControllerOptions = {}) {
    this.viewer = viewer;
    this.commandOptions = commandOptions;
    this.solver = new ReferencePlaneSolver(this);
    this.tolerance = {
      ...DEFAULT_PLANE_TOLERANCE,
      ...(commandOptions.tolerance ?? {}),
    };
  }

  /**
   * 获取当前视图模型。
   *
   * @returns 视图模型。
   */
  public getViewModel(): PlaneCommandViewModel {
    return {
      state: this.state,
      canSubmit: this.state.preview.ok && this.state.solveStatus === PlaneSolveStatus.FullyDefined,
    };
  }


    /**
   * 判断当前命令是否为编辑特征会话。
   *
   * @returns 编辑特征会话返回 `true`。
   */
  public isEditingFeature(): boolean {
    return this.editSession !== null;
  }

  /**
   * 规范化基准面编辑提交前的历史快照。
   *
   * 设计说明：
   * 1. 编辑态会临时隐藏正在编辑的原基准面，避免与预览面片重叠；
   * 2. 若直接把该临时状态写入 Undo 前快照，撤销会回到“被隐藏”的错误可见性；
   * 3. 因此在提交历史前统一把目标基准面的可见性回填为进入编辑前状态。
   *
   * @param snapshot 提交前采集的基准面历史快照。
   * @returns 已归一化的历史快照。
   */
  public normalizeHistorySnapshotBeforeSubmit(snapshot: DatumPlaneHistorySnapshot): DatumPlaneHistorySnapshot {
    const activeEditSession = this.editSession;
    if (!activeEditSession) {
      return snapshot;
    }

    return {
      ...snapshot,
      planes: snapshot.planes.map((planeRecord) => (
        planeRecord.planeId === activeEditSession.planeId
          ? {
            ...planeRecord,
            visible: activeEditSession.previousVisible,
          }
          : planeRecord
      )),
    };
  }


    /**
   * 打开基准面命令。
   */
  public open(): void {
    this.pendingCreationContext = resolveReferenceCreationContextFromViewerMode(this.viewer.getMode());
    this.editSession = null;
    this.state = {
      ...createDefaultStateInternal(),
      lifecycle: ReferenceCommandLifecycle.Open,
      dirty: false,
    };
    this.recomputePreview();
    this.viewer.entitySelectionManager?.clearSelectionProgrammatically();
    this.viewer.setMode('reference-plane' as never);
    this.viewer.requestRender();
  }


    /**
   * 打开已有基准面的编辑特征会话。
   *
   * 设计说明：
   * 1. 编辑特征必须恢复原 PropertyManager 参数，而不是从当前几何位置反推；
   * 2. `calculationSource` 是基准面创建时保存的唯一稳定定义来源；
   * 3. 引用丢失时直接拒绝进入编辑，避免 UI 进入半恢复状态。
   *
   * @param planeId 待编辑的基准面 ID。
   * @returns 打开结果。
   */
  public openEdit(planeId: number): { ok: boolean; reason?: string } {
    const datumPlane = this.viewer.getDatumPlane(planeId);
    if (!datumPlane) {
      return { ok: false, reason: '未找到目标基准面，无法编辑' };
    }

    const calculationSource = datumPlane.getCalculationSource();
    if (!calculationSource) {
      return { ok: false, reason: '默认或导入基准面不支持编辑特征' };
    }

    const restoredState = this.restoreCalculationSourceToState(calculationSource);
    if (!restoredState.ok) {
      return restoredState;
    }

    this.pendingCreationContext = calculationSource.creationContext;
    this.editSession = {
      planeId,
      featureId: calculationSource.featureId,
      generatedPlaneIndex: calculationSource.generatedPlaneIndex,
      initialConstraint: restoredState.state.constraint,
      previousVisible: datumPlane.isVisible(),
    };
    this.state = restoredState.state;
    // 编辑态先隐藏原基准面，避免与重新求解出的预览面叠加显示。
    datumPlane.setVisible(false);
    this.recomputePreview();
    this.syncSelectionVisuals();
    this.viewer.setMode('reference-plane' as never);
    this.viewer.requestRender();
    return { ok: true };
  }


    /**
   * 取消基准面命令。
   */
  public cancel(): void {
    const exitViewerMode = resolveReferenceCommandExitViewerMode(this.pendingCreationContext);

        const activeEditSession = this.editSession;
    this.state = createDefaultStateInternal();
    this.pendingCreationContext = ReferenceCreationContext.PartFeature;
    if (activeEditSession) {
      const currentPlane = this.viewer.getDatumPlane(activeEditSession.planeId);
      currentPlane?.setVisible(activeEditSession.previousVisible);
    }
    this.editSession = null;
    this.clearPreview();
    this.commandOptions.onCancelled?.();
    this.viewer.entitySelectionManager?.clearSelectionProgrammatically();
    this.viewer.setMode(exitViewerMode as never);
    this.viewer.requestRender();
  }

  /**
   * 提交基准面命令。
   *
   * @returns 提交结果。
   */
  public submit(): { ok: boolean; featureId?: string; reason?: string } {
    const viewModel = this.getViewModel();
    if (!viewModel.canSubmit || !this.state.preview.result) {
      return {
        ok: false,
        reason: this.state.preview.error ?? '当前基准面条件不足，无法提交',
      };
    }

    const activeEditSession = this.editSession;
    const featureId = activeEditSession?.featureId ?? createPlaneFeatureId();
    const result = this.state.preview.result;
    const definition = buildDefinitionInternal(featureId, this.state);

      const exitViewerMode = resolveReferenceCommandExitViewerMode(this.pendingCreationContext);


      // 基准面正式提交时沿用当前预览解，避免提交与预览出现结果偏差。
    if (activeEditSession) {
      const updated = this.updateDatumPlaneFromFeatureResult({
        planeId: activeEditSession.planeId,
        featureId,
        result,
        definition,
        generatedPlaneIndex: activeEditSession.generatedPlaneIndex,
        visible: activeEditSession.previousVisible,
      });
      if (!updated) {
        return {
          ok: false,
          reason: '基准面编辑提交失败，请检查目标基准面是否仍然存在',
        };
      }
    } else {
      this.createDatumPlaneFromFeatureResult(featureId, result, definition);
    }

    this.state = createDefaultStateInternal();
    this.pendingCreationContext = ReferenceCreationContext.PartFeature;
    this.editSession = null;
    this.clearPreview();
    this.viewer.entitySelectionManager?.clearSelectionProgrammatically();
    this.viewer.setMode(exitViewerMode as never);
    this.viewer.requestRender();

    return {
      ok: true,
      featureId,
    };
  }


    /**
   * 重算装配体上下文创建的基准面。
   *
   * 设计说明：
   * 1. 装配体基准面保存的是计算来源，而不是一次性静态平面；
   * 2. 移动零部件后，导入拓扑代理的 `uuid/name` 可能已经失效，必须通过稳定拓扑引用重新解析；
   * 3. 本方法只处理带有计算来源的装配体基准面，默认基准面和零件内基准面不受影响。
   *
   * @returns 成功重算的基准面数量。
   */
  public recomputeAssemblyDatumPlanes(): number {
    let recomputedCount = 0;

    this.viewer.getAllDatumPlanes().forEach((datumPlane) => {
      const calculationSource = datumPlane.getCalculationSource();
      if (!calculationSource || calculationSource.creationContext !== ReferenceCreationContext.Assembly) {
        return;
      }

      recomputedCount += this.recomputeDatumPlaneFromCalculationSource(datumPlane) ? 1 : 0;
    });

    if (recomputedCount > 0) {
      this.viewer.requestRender();
    }
    return recomputedCount;
  }


    /**
   * 重算零件特征上下文中依赖特征几何的基准面。
   *
   * 设计说明：
   * 1. 特征 replay 会先清空并重建结果体，依赖特征面、边、点的基准面必须在结果体同步完成后复算；
   * 2. 默认基准面、无计算来源基准面和仅依赖基准几何的零件基准面不受特征结果体变化影响；
   * 3. 这里复用正式基准面保存的计算来源，避免为重建链路复制一套求解定义。
   *
   * @returns 成功重算的基准面数量。
   */
  public recomputePartFeatureDatumPlanes(): number {
    let recomputedCount = 0;

    this.viewer.getAllDatumPlanes().forEach((datumPlane) => {
      const calculationSource = datumPlane.getCalculationSource();
      if (
        !calculationSource
        || calculationSource.creationContext !== ReferenceCreationContext.PartFeature
        || !this.hasFeatureGeometryPlaneReference(calculationSource.definition)
      ) {
        return;
      }

      recomputedCount += this.recomputeDatumPlaneFromCalculationSource(datumPlane) ? 1 : 0;
    });

    if (recomputedCount > 0) {
      this.viewer.requestRender();
    }
    return recomputedCount;
  }


    /**
   * 按场景对象写入一个基准面参考选择。
   *
   * @param object 场景对象。
   * @param label 显示名称。
   * @param entityType 实体类型。
   * @returns 是否接收本次选择。
   */
  public addSelectionByObject(
    object: PlaneSelectableObject,
    label?: string,
    entityType?: ReferenceEntitySemanticType,
  ): boolean {
    const resolvedSelection = this.resolveSelectionFromObject(object);
    const normalizedEntityType = entityType
      ?? (resolvedSelection.entityType === 'unknown' ? null : resolvedSelection.entityType);
    if (!normalizedEntityType || resolvedSelection.entityType === 'unknown') {
      return false;
    }
    const entityId = this.resolveSelectionEntityId(object);
    const candidate: PlaneSelectionCandidate = {
      entityId,
      entityType: normalizedEntityType,
      label: label ?? object.name ?? entityId,
      kind: normalizedEntityType,
      payload: buildSelectionReferencePayload(object),
    };
    const accepted = this.addSelection(candidate);
    return accepted;
  }

  /**
   * 添加一个基准面选择候选。
   *
   * @param candidate 选择候选。
   * @returns 是否接收本次选择。
   */
  public addSelection(candidate: PlaneSelectionCandidate): boolean {
    if (this.state.lifecycle !== ReferenceCommandLifecycle.Open) {
      return false;
    }

    if (this.shouldRejectSelectionCandidate(candidate)) {
      this.applyInvalidSelectionState(INVALID_REFERENCE_COMBINATION_MESSAGE);
      this.viewer.requestRender();
      return false;
    }

    const existingSlot = this.findSlotByEntityId(candidate.entityId);
    if (existingSlot) {
      // 再次选择已存在引用时执行取消选中语义，而不是重复占用新槽位。
      this.state.refs[existingSlot] = null;
      this.compactReferenceSlots();
      this.state.activeSlot = this.resolveNextActiveSlotAfterOpen();
      this.state.dirty = true;
    } else {
      this.state.refs[this.state.activeSlot] = candidate;
      this.state.activeSlot = this.resolveNextActiveSlotAfterOpen();
      this.state.dirty = true;
    }


      this.reconcileConstraintAfterReferenceMutation();


      this.recomputePreview();
    this.viewer.requestRender();
    return true;
  }


  /**
   * 同步当前选择的场景高亮。
   */
  public syncSelectionVisuals(): void {
    const refs = this.getViewModel().state.refs;
    const selectedObjects = [PlaneSlotKey.First, PlaneSlotKey.Second, PlaneSlotKey.Third]
      .map((slot) => refs[slot]?.entityId ?? null)
      .filter((entityId): entityId is string => typeof entityId === 'string' && entityId.length > 0)
      .map((entityId) => this.resolveSelectionObject(entityId))
      .filter((object): object is THREE.Object3D => object !== null);
    this.viewer.entitySelectionManager?.updateSelectionState(
      selectedObjects.map((object) => ({
        distance: 0,
        object,
        point: object.getWorldPosition(new THREE.Vector3()),
      })),
    );
    this.viewer.requestRender();
  }

  /**
   * 设置当前激活槽位。
   *
   * @param slot 槽位键值。
   */
  public setActiveSlot(slot: PlaneSlotKey): void {
    this.state.activeSlot = slot;
  }

  /**
   * 获取基准面求解容差。
   *
   * @returns 当前控制器持有的容差策略。
   */
  public getTolerance(): PlaneTolerancePolicy {
    return this.tolerance;
  }

  /**
   * 读取当前相机在世界坐标中的朝上方向。
   *
   * @returns 相机朝上方向；不可用时返回 `null`。
   */
  public getCurrentCameraUpDirection(): THREE.Vector3 | null {
    const camera = this.viewer.getCamera();
    if (!camera) {
      return null;
    }
    return new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion).normalize();
  }

  /**
   * 读取当前相机的观察方向。
   *
   * @returns 相机观察方向；不可用时返回 `null`。
   */
  public getCurrentViewDirection(): THREE.Vector3 | null {
    const camera = this.viewer.getCamera();
    if (!camera) {
      return null;
    }
    return camera.getWorldDirection(new THREE.Vector3()).normalize();
  }

  /**
   * 设置约束类型。
   *
   * @param constraint 目标约束。
   */
  public setConstraint(constraint: PlaneConstraintType): void {
    const previousConstraint = this.state.constraint;
    this.state.constraint = constraint;
    this.state.modeSource = ReferenceCommandModeSource.ManualLocked;
    this.applyConstraintDefaultScalar(previousConstraint);
    this.state.dirty = true;
    this.recomputePreview();
  }

  /**
   * 设置距离或角度值。
   * @param value 输入值。
   */
  public setDistanceOrAngle(value: number): void {
    this.state.distanceOrAngle = Number.isFinite(value) ? value : 0;
    this.state.dirty = true;
    this.recomputePreview();
  }

  /**
   * 设置投影类型。
   *
   * @param value 投影类型。
   */
  public setProjectionType(value: PlaneProjectionType | null): void {
    this.state.projectionType = value;
    this.state.dirty = true;
    this.recomputePreview();
  }

  /**
   * 设置解索引。
   *
   * @param value 解索引。
   */
  public setSolutionIndex(value: number): void {
    this.state.solutionIndex = Math.max(0, Math.floor(Number.isFinite(value) ? value : 0));
    this.state.dirty = true;
    this.recomputePreview();
  }

  /**
   * 设置平面数量。
   *
   * @param value 平面数量。
   */
  public setPlaneCount(value: number): void {
    this.state.planeCount = Math.max(1, Math.floor(Number.isFinite(value) ? value : 1));
    this.state.dirty = true;
    this.recomputePreview();
  }

  /**
   * 设置法向翻转状态。
   *
   * @param value 是否翻转。
   */
  public setReverseNormal(value: boolean): void {
    this.state.reverseNormal = value;
    this.state.dirty = true;
    this.recomputePreview();
  }

  /**
   * 设置偏移翻转状态。
   *
   * @param value 是否翻转。
   */
  public setReverseOffset(value: boolean): void {
    this.state.reverseOffset = value;
    this.state.dirty = true;
    this.recomputePreview();
  }

  /**
   * 设置原点是否约束在线上。
   *
   * @param value 是否开启。
   */
  public setOriginOnCurve(value: boolean): void {
    this.state.originOnCurve = value;
    this.state.dirty = true;
    this.recomputePreview();
  }

  /**
   * 移除指定引用。
   *
   * @param entityId 实体 ID。
   */
  public removeSelection(entityId: string): void {
    const slot = this.findSlotByEntityId(entityId);
    if (!slot) {
      return;
    }

    this.state.refs[slot] = null;
    this.compactReferenceSlots();
    this.state.activeSlot = this.resolveNextActiveSlotAfterOpen();

      this.reconcileConstraintAfterReferenceMutation();


      this.state.dirty = true;
    this.recomputePreview();
  }

  /**
   * 清空全部引用。
   */
  public clearSelections(): void {
    this.state.refs[PlaneSlotKey.First] = null;
    this.state.refs[PlaneSlotKey.Second] = null;
    this.state.refs[PlaneSlotKey.Third] = null;
    this.state.activeSlot = PlaneSlotKey.First;

      this.reconcileConstraintAfterReferenceMutation();

      this.state.dirty = true;
    this.recomputePreview();
  }

  /**
   * 获取当前可用约束集合。
   *
   * @returns 可用约束列表。
   */
  public getAvailableConstraints() {
    return availableConstraintsForStateInternal(this.state);
  }

  /**
   * 释放预览资源。
   */
  public dispose(): void {
    this.clearPreview();

      this.previewNormalArrowDragSubscription?.unsubscribe();
    this.previewNormalArrowDragSubscription = null;

  }

  /**
   * 处理预览变化。
   *
   * @param preview 预览状态。
   */
  private handlePreviewChanged(preview: PlanePreviewState): void {
    if (!preview.ok || !preview.result || preview.result.planes.length === 0) {
      this.clearPreview();
      this.viewer.requestRender();
      return;
    }
    this.renderPreview(preview.result);
    this.viewer.requestRender();
  }

    /**
   * 解析基准面参考实体。
   *
   * @param reference 参考实体引用。
   * @returns 成功时返回已解析实体，否则返回 `null`。
   */
  public resolveReference(reference: PlaneEntityReference): PlaneResolvedEntity | null {

        const topologyReference = this.resolveTopologyPriorityPlaneReference(reference);
    if (topologyReference) {
      return topologyReference;
    }


        const datumPlaneId = parseDatumReferenceEntityId(reference.entityId, DatumReferenceEntityKind.Plane);
    if (datumPlaneId !== null) {
      return this.viewer.getDatumPlane(datumPlaneId);
    }

    const datumAxisId = parseDatumReferenceEntityId(reference.entityId, DatumReferenceEntityKind.Axis);
    if (datumAxisId !== null) {
      return this.viewer.getDatumAxis(datumAxisId);
    }

    const datumPointId = parseDatumReferenceEntityId(reference.entityId, DatumReferenceEntityKind.Point);
    if (datumPointId !== null) {
      return this.viewer.getDatumPoint(datumPointId);
    }

    const object = this.viewer.getSceneObjectById(reference.entityId);
    if (object && (isFeatureFace(object) || isFeatureLine(object) || isFeaturePoint(object) || isDatumAxis(object) || isDatumPoint(object))) {
      return object;
    }

    return null;
  }

  /**
   * 渲染预览基准面。
   *
   * @param result 预览求解结果。
   */
  private renderPreview(result: PlaneFeatureResult): void {

      this.ensurePreviewNormalArrowDragSubscription();


      this.clearPreview();
    const group = new THREE.Group();
    group.name = '__reference_plane_preview__';
    const color = result.solveStatus === PlaneSolveStatus.FullyDefined
      ? 0x2aa6ff
      : 0xff4d4f;

    const activeIndex = Math.max(0, Math.min(result.planes.length - 1, result.activeSolutionIndex ?? 0));

    /**
     * 多输出模式（solutionCount === 1 && planes.length > 1）：
     * 由 planeCount 扩展产生，所有平面均为最终输出，需全部渲染。
     * 几何多候选解（solutionCount > 1）：仅渲染当前激活解，避免候选结果同时铺开干扰判断。
     */
    const isMultiOutput = result.solutionCount === 1 && result.planes.length > 1;
    const framesToRender = isMultiOutput ? result.planes : [result.planes[activeIndex]].filter(Boolean);

    if (framesToRender.length === 0) {
      this.previewGroup = group;
      this.viewer.scene.add(group);
      return;
    }

    framesToRender.forEach((frame, frameIndex) => {
      const displaySize = resolvePreviewDatumPlaneDisplaySize(frame.displayWidth, frame.displayHeight, 20);
      const { width, height } = displaySize;
      const geometry = new THREE.PlaneGeometry(width, height);
      const material = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.22,
        side: THREE.DoubleSide,
        depthTest: false,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.copy(frame.origin);

      const xDir = frame.xDir.clone().normalize();
      const yDir = frame.yDir.clone().normalize();
      const zDir = frame.normal.clone().normalize();
      const basis = new THREE.Matrix4().makeBasis(xDir, yDir, zDir);
      mesh.quaternion.setFromRotationMatrix(basis);
      mesh.renderOrder = 990;
      group.add(mesh);


        // 法线预览复用线性阵列方向箭头，避免基准面命令继续维护独立线段视觉规则。
      const patternDirectionArrowManager = this.viewer.patternDirectionArrowManager;
      if (!patternDirectionArrowManager) {
        return;
      }
      const normalArrowSize = resolveReferencePlanePreviewNormalArrowSize(width, height);
      const arrowSlotKey = `${REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_SLOT_PREFIX}${frameIndex}`;
      this.previewNormalArrowSlots.add(arrowSlotKey);
      patternDirectionArrowManager.setArrow(arrowSlotKey, {
        color,
        anchorWorld: frame.origin.clone(),
        directionWorld: zDir,
        shaftRadius: normalArrowSize.shaftRadius,
        shaftLength: normalArrowSize.shaftLength,
        headRadius: normalArrowSize.headRadius,
        headLength: normalArrowSize.headLength,
        bottomLength: normalArrowSize.bottomLength,
        topLength: normalArrowSize.topLength,
        anchorLift: normalArrowSize.anchorLift,
        axisDrag: {
          enabled: this.canDragDistanceOffsetFromPreviewArrow(),
          contextKey: REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_DRAG_CONTEXT,
          scalarValue: this.state.distanceOrAngle,
          sceneUnitToScalarScale: this.resolveDistanceDragSceneUnitToScalarScale(),
        },
        pixelSize: {
          shaftRadiusPixels: REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_SHAFT_RADIUS_PIXELS,
          shaftLengthPixels: REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_SHAFT_LENGTH_PIXELS,
          headRadiusPixels: REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_HEAD_RADIUS_PIXELS,
          headLengthPixels: REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_HEAD_LENGTH_PIXELS,
          bottomLengthPixels: REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_BOTTOM_LENGTH_PIXELS,
          topLengthPixels: REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_TOP_LENGTH_PIXELS,
          anchorLiftPixels: REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_ANCHOR_LIFT_PIXELS,
          minWorldSize: 1e-5,
        },
      });
      const managedArrow = this.resolvePreviewNormalArrowObject(arrowSlotKey);
      managedArrow?.traverse((object) => {
        object.renderOrder = REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_RENDER_ORDER;
      });

    });

    this.previewGroup = group;
        this.viewer.addObject(group);
  }

  /**
   * 清理预览对象与 GPU 资源。
   */
  private clearPreview(): void {

      this.clearPreviewNormalArrows();

      if (!this.previewGroup) {
      return;
    }
    this.viewer.scene.remove(this.previewGroup);
    this.previewGroup.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (mesh.geometry instanceof THREE.BufferGeometry) {
        mesh.geometry.dispose();
      }
      const material = (mesh as unknown as { material?: THREE.Material | THREE.Material[] }).material;
      if (Array.isArray(material)) {
        material.forEach((item) => item.dispose());
      } else if (material instanceof THREE.Material) {
        material.dispose();
      }
    });
    this.previewGroup = null;
  }

  /**
   * 判断当前预览箭头是否可以拖拽偏移距离。
   *
   * @returns 可以拖拽时返回 `true`。
   */
  private canDragDistanceOffsetFromPreviewArrow(): boolean {
    return (
      this.state.lifecycle === ReferenceCommandLifecycle.Open
      && this.state.constraint === PlaneConstraintType.Distance
      && Boolean(this.state.preview.ok)
    );
  }

  /**
   * 解析基准面偏移拖拽的场景单位到标量单位换算比例。
   *
   * @returns 换算比例。
   */
  private resolveDistanceDragSceneUnitToScalarScale(): number {
    return 1;
  }

  /**
   * 处理基准面预览法线箭头轴向拖拽。
   *
   * @param event 拖拽事件。
   */
  private handlePreviewNormalArrowAxisDrag(event: PatternDirectionArrowAxisDragEvent): void {
    if (
      event.contextKey !== REFERENCE_PLANE_PREVIEW_NORMAL_ARROW_DRAG_CONTEXT
      || event.phase !== PatternDirectionArrowAxisDragPhase.Change
      || !this.canDragDistanceOffsetFromPreviewArrow()
    ) {
      return;
    }

    // 拖拽只改变 Distance 约束的偏移标量，后续预览重算仍走原有控制器链路。
    this.setDistanceOrAngle(event.scalarValue);
  }

  /**
   * 清理当前基准面预览法线箭头。
   */
  private clearPreviewNormalArrows(): void {
    const patternDirectionArrowManager = this.viewer.patternDirectionArrowManager;
    if (!patternDirectionArrowManager) {
      this.previewNormalArrowSlots.clear();
      return;
    }
    this.previewNormalArrowSlots.forEach((slotKey) => {
      patternDirectionArrowManager.clearArrow(slotKey);
    });
    this.previewNormalArrowSlots.clear();
  }

  /**
   * 解析指定槽位的托管预览法线箭头对象。
   *
   * @param slotKey 槽位键。
   * @returns 箭头对象。
   */
  private resolvePreviewNormalArrowObject(slotKey: string): THREE.Object3D | null {
    const patternDirectionArrowManager = this.viewer.patternDirectionArrowManager;
    if (!patternDirectionArrowManager) {
      return null;
    }
    let resolved: THREE.Object3D | null = null;
    patternDirectionArrowManager.traverse((object) => {
      if (!resolved && object.name === slotKey) {
        resolved = object;
      }
    });
    return resolved;
  }

    /**
   * 确保基准面预览法线箭头拖拽事件已订阅。
   */
  private ensurePreviewNormalArrowDragSubscription(): void {
    if (this.previewNormalArrowDragSubscription || !this.viewer.patternDirectionArrowManager) {
      return;
    }

    // Viewer 构造阶段会先创建 ReferencePlaneController，再创建方向箭头管理器；
    // 因此订阅必须惰性建立，避免构造期访问尚未初始化的 manager。
    this.previewNormalArrowDragSubscription = this.viewer.patternDirectionArrowManager
      .observeAxisDrag()
      .subscribe((event) => this.handlePreviewNormalArrowAxisDrag(event));
  }

  /**
   * 获取当前模型包围盒。
   *
   * @returns 成功时返回包围盒最小/最大点，否则返回 `null`。
   */
  public getModelBoundingBox(): { min: THREE.Vector3; max: THREE.Vector3 } | null {
    const sceneBounds = new THREE.Box3();
    this.viewer.scene.updateMatrixWorld(true);
    this.viewer.scene.traverse((object) => {
      if (!this.shouldIncludeObjectInModelBoundingBox(object)) {
        return;
      }
      const geometry = (object as THREE.Mesh).geometry;
      if (!geometry.boundingBox) {
        geometry.computeBoundingBox();
      }
      const objectBounds = geometry.boundingBox?.clone();
      if (!objectBounds) {
        return;
      }
      objectBounds.applyMatrix4(object.matrixWorld);
      sceneBounds.union(objectBounds);
    });

    if (sceneBounds.isEmpty()) {
      return null;
    }
    return {
      min: sceneBounds.min.clone(),
      max: sceneBounds.max.clone(),
    };
  }

    /**
   * 将基准面计算来源恢复为命令状态。
    if (this.editSession) {
      const currentPlane = this.viewer.getDatumPlane(this.editSession.planeId);
      currentPlane?.setVisible(this.editSession.previousVisible);
    }
   *
   * @param calculationSource 基准面计算来源。
   * @returns 恢复后的命令状态。
   */
  private restoreCalculationSourceToState(
    calculationSource: ReferencePlaneCalculationSource,
  ): { ok: true; state: PlaneCommandState } | { ok: false; reason: string } {
    const definition = calculationSource.definition;
    const nextState: PlaneCommandState = {
      ...createDefaultStateInternal(),
      lifecycle: ReferenceCommandLifecycle.Open,
      modeSource: ReferenceCommandModeSource.ManualLocked,
      constraint: this.resolveConstraintFromDefinition(definition),
      projectionType: definition.projectionType,
      distanceOrAngle: this.resolveDistanceOrAngleFromDefinition(definition),
      reverseNormal: resolveFeatureReferenceFlag(definition.refs, 'reverseNormal'),
      reverseOffset: resolveFeatureReferenceFlag(definition.refs, 'reverseOffset'),
      originOnCurve: resolveFeatureReferenceFlag(definition.refs, 'originOnCurve'),
      planeCount: Math.max(1, Math.floor(definition.planeCount)),
      solutionIndex: Math.max(0, Math.floor(definition.solutionIndex ?? calculationSource.selectedSolutionIndex ?? 0)),
      dirty: false,
    };

    for (const reference of definition.refs) {
      const candidate = this.restoreSelectionCandidateFromReference(reference, calculationSource);
      if (!candidate) {
        return { ok: false, reason: '参考引用丢失，无法编辑' };
      }
      nextState.refs[slotFromRole(reference.role)] = candidate;
    }

    nextState.activeSlot = this.resolveNextActiveSlotForState(nextState);
    return { ok: true, state: nextState };
  }

  /**
   * 从特征引用恢复选择候选。
   *
   * @param reference 特征引用。
   * @param calculationSource 基准面计算来源。
   * @returns 选择候选；引用无法解析时返回 `null`。
   */
  private restoreSelectionCandidateFromReference(
    reference: PlaneFeatureReference,
    calculationSource: ReferencePlaneCalculationSource,
  ): PlaneSelectionCandidate | null {
    if (!this.resolveReference(reference.entityRef)) {
      return null;
    }

    const calculationReference = calculationSource.references.find((item) => (
      item.role === reference.role
      || item.entityId === reference.entityRef.entityId
    ));

    return {
      entityId: reference.entityRef.entityId,
      entityType: reference.entityRef.entityType,
      label: calculationReference?.label ?? reference.entityRef.entityId,
      kind: reference.entityKind,
      payload: {
        sourceObjectUuid: calculationReference?.sourceObjectUuid,
        sourceObjectName: calculationReference?.sourceObjectName,
        topologyPick: reference.entityRef.topologyPick ?? calculationReference?.topologyPick ?? null,
        assemblyTopologyRef: reference.entityRef.assemblyTopologyRef ?? calculationReference?.assemblyTopologyRef ?? null,
      },
    };
  }

  /**
   * 从定义恢复距离或角度标量。
   *
   * @param definition 特征定义。
   * @returns 距离或角度值。
   */
  private resolveDistanceOrAngleFromDefinition(definition: PlaneFeatureDefinition): number {
    const scalarRef = definition.refs.find((reference) => Number.isFinite(reference.scalar));
    return Number(scalarRef?.scalar ?? 0);
  }

  /**
   * 在指定状态内解析下一个激活槽位。
   *
   * @param state 命令状态。
   * @returns 下一个激活槽位。
   */
  private resolveNextActiveSlotForState(state: PlaneCommandState): PlaneSlotKey {
    if (!state.refs[PlaneSlotKey.First]) {
      return PlaneSlotKey.First;
    }
    if (!state.refs[PlaneSlotKey.Second]) {
      return PlaneSlotKey.Second;
    }
    if (!state.refs[PlaneSlotKey.Third]) {
      return PlaneSlotKey.Third;
    }
    return PlaneSlotKey.Third;
  }

  /**
   * 使用求解结果更新既有基准面。
   *
   * @param input 更新参数。
   * @returns 是否更新成功。
   */
  private updateDatumPlaneFromFeatureResult(input: {
    /** 待更新基准面 ID。 */
    planeId: number;
    /** 基准面特征 ID。 */
    featureId: string;
    /** 当前求解结果。 */
    result: PlaneFeatureResult;
    /** 正式特征定义。 */
    definition: PlaneFeatureDefinition;
    /** 多输出基准面中的原输出索引。 */
    generatedPlaneIndex: number;
    /** 更新后需要恢复的可见性。 */
    visible: boolean;
  }): boolean {
    const isMultiOutput = input.result.solutionCount === 1 && input.result.planes.length > 1;
    const activeIndex = Math.max(0, Math.min(input.result.planes.length - 1, input.result.activeSolutionIndex ?? 0));
    const framesToUpdate = isMultiOutput ? input.result.planes : [input.result.planes[activeIndex]].filter(Boolean);
    const generatedPlaneIndex = isMultiOutput
      ? Math.max(0, Math.min(framesToUpdate.length - 1, input.generatedPlaneIndex))
      : 0;
    const frame = framesToUpdate[generatedPlaneIndex];
    if (!frame) {
      return false;
    }

    const width = resolveCreatedDatumPlaneDisplaySize(frame.displayWidth);
    const height = resolveCreatedDatumPlaneDisplaySize(frame.displayHeight);
    const calculationSource = buildCalculationSourceInternal({
      featureId: input.featureId,
      state: this.state,
      definition: input.definition,
      creationContext: this.pendingCreationContext,
      result: input.result,
      generatedPlaneIndex,
      generatedPlaneCount: framesToUpdate.length,
    });

    const updatedPlane = this.viewer.datumPlaneManager?.updatePlane(input.planeId, {
      creationContext: this.pendingCreationContext,
      name: '',
      normal: frame.normal.clone(),
      originWorld: frame.origin.clone(),
      xAxis: frame.xDir.clone(),
      yAxis: frame.yDir.clone(),
      width,
      height,
      opacity: 0.3,
      visible: input.visible,
      calculationSource,
    }) ?? null;

    return Boolean(updatedPlane);
  }


    /**
   * 判断场景对象是否应参与模型包围盒计算。
   *
   * @param object 场景对象。
   * @returns 应参与模型几何包围盒时返回 true。
   */
  private shouldIncludeObjectInModelBoundingBox(object: THREE.Object3D): object is THREE.Mesh {
    if (Reflect.get(object, 'isMesh') !== true) {
      return false;
    }
    const geometry = (object as THREE.Mesh).geometry;
    if (!geometry) {
      return false;
    }
    if (isDatumPlaneObject(object) || isDatumPlaneMesh(object) || getDatumPlaneIdFromObject(object) !== null) {
      return false;
    }
    if (isDatumAxis(object) || isDatumPoint(object) || isFeatureFace(object) || isFeatureLine(object) || isFeaturePoint(object)) {
      return false;
    }
    if (this.isInsideReferencePreviewGroup(object)) {
      return false;
    }
    if (object.userData?.skipSelection || object.userData?.isDatumPlaneDebugBox || object.userData?.isGlbDatumSlotOverlay) {
      return false;
    }
    return true;
  }

  /**
   * 判断对象是否处于基准面预览组内。
   *
   * @param object 场景对象。
   * @returns 位于预览组内时返回 true。
   */
  private isInsideReferencePreviewGroup(object: THREE.Object3D): boolean {
    let current: THREE.Object3D | null = object;
    while (current) {
      if (current.name === '__reference_plane_preview__') {
        return true;
      }
      current = current.parent;
    }
    return false;
  }

  /**
   * 根据求解结果创建正式基准面对象。
   *
   * @param featureId 特征 ID。
   * @param result 求解结果。
   * @param definition 正式特征定义。
   */
  private createDatumPlaneFromFeatureResult(
    featureId: string,
    result: PlaneFeatureResult,
    definition: PlaneFeatureDefinition,
  ): void {
    /**
     * 多输出模式（solutionCount === 1 && planes.length > 1）：
     * 由 planeCount 扩展产生，所有平面均需独立创建为基准面对象。
     * 几何多候选解（solutionCount > 1）：仅创建用户选定的激活解对应的基准面。
     */
    const isMultiOutput = result.solutionCount === 1 && result.planes.length > 1;
    const activeIndex = Math.max(0, Math.min(result.planes.length - 1, result.activeSolutionIndex ?? 0));
    const framesToCreate = isMultiOutput ? result.planes : [result.planes[activeIndex]].filter(Boolean);

    framesToCreate.forEach((frame, generatedPlaneIndex) => {
      const width = resolveCreatedDatumPlaneDisplaySize(frame.displayWidth);
      const height = resolveCreatedDatumPlaneDisplaySize(frame.displayHeight);
      const calculationSource = buildCalculationSourceInternal({
        featureId,
        state: this.state,
        definition,
        creationContext: this.pendingCreationContext,
        result,
        generatedPlaneIndex,
        generatedPlaneCount: framesToCreate.length,
      });
      this.viewer.createDatumPlane({
        creationContext: this.pendingCreationContext,
        name: '',
        normal: frame.normal.clone(),
        originWorld: frame.origin.clone(),
        xAxis: frame.xDir.clone(),
        yAxis: frame.yDir.clone(),
        width,
        height,
        visible: true,
        opacity: 0.3,
        calculationSource,
      });
    });
  }

  /**
   * 从场景对象解析选择分类。
   *
   * @param object 场景对象。
   * @returns 选择分类与实体类型。
   */
  private resolveSelectionFromObject(object: THREE.Object3D | null): {
    kind: ReferenceEntitySemanticType | 'unknown';
    entityType: ReferenceEntitySemanticType | 'unknown';
  } {
    if (!object) {
      return { kind: 'unknown', entityType: 'unknown' };
    }
    if (getDatumPlaneIdFromObject(object) !== null) {
      return { kind: ReferenceEntitySemanticType.DatumPlane, entityType: ReferenceEntitySemanticType.DatumPlane };
    }

      if (isDatumAxis(object)) {
      return { kind: ReferenceEntitySemanticType.DatumAxis, entityType: ReferenceEntitySemanticType.DatumAxis };
    }

      if (isDatumPoint(object)) {
      return { kind: ReferenceEntitySemanticType.DatumPoint, entityType: ReferenceEntitySemanticType.DatumPoint };
    }

      if (
      Reflect.get(object, 'isFeatureSurface') === true
      && Reflect.get(object, 'surfaceKind') !== 'plane'
    ) {
      // 平面型 FeatureSurface 必须继续走平面链路，避免“曲面+平面”被误判为“曲面+曲面”。
      return { kind: ReferenceEntitySemanticType.FeatureSurface, entityType: ReferenceEntitySemanticType.FeatureSurface };
    }

      if (isFeatureFace(object)) {
      // 非平面 FeatureSurface 必须进入曲面链路，避免被错误当成普通平面面。
      if (isNonPlanarFeatureSurfaceObject(object)) {
        return { kind: ReferenceEntitySemanticType.FeatureSurface, entityType: ReferenceEntitySemanticType.FeatureSurface };
      }
      return { kind: ReferenceEntitySemanticType.FeatureFace, entityType: ReferenceEntitySemanticType.FeatureFace };
    }
    if (isFeatureLine(object)) {
      return { kind: ReferenceEntitySemanticType.FeatureLine, entityType: ReferenceEntitySemanticType.FeatureLine };
    }
    if (isFeaturePoint(object)) {
      return { kind: ReferenceEntitySemanticType.FeaturePoint, entityType: ReferenceEntitySemanticType.FeaturePoint };
    }
    return { kind: 'unknown', entityType: 'unknown' };
  }

  /**
   * 解析对象对应的选择实体 ID。
   *
   * @param object 场景对象。
   * @returns 选择实体 ID。
   */
  private resolveSelectionEntityId(object: THREE.Object3D): string {
    const datumPlaneId = getDatumPlaneIdFromObject(object);
    if (datumPlaneId !== null) {
      return buildDatumReferenceEntityId(DatumReferenceEntityKind.Plane, datumPlaneId);
    }
    if (isDatumAxis(object)) {
      return buildDatumReferenceEntityId(DatumReferenceEntityKind.Axis, object.datumAxisId);
    }
    if (isDatumPoint(object)) {
      return buildDatumReferenceEntityId(DatumReferenceEntityKind.Point, object.datumPointId);
    }
    return object.uuid;
  }

  /**
   * 解析引用实体对应的场景对象。
   *
   * @param entityId 引用实体 ID。
   * @returns 场景对象；不存在时返回 `null`。
   */
  private resolveSelectionObject(entityId: string): THREE.Object3D | null {
    const datumPlaneId = parseDatumReferenceEntityId(entityId, DatumReferenceEntityKind.Plane);
    if (datumPlaneId !== null) {
      return this.viewer.getDatumPlane(datumPlaneId)?.getObject3D() ?? null;
    }
    const datumAxisId = parseDatumReferenceEntityId(entityId, DatumReferenceEntityKind.Axis);
    if (datumAxisId !== null) {
      return this.viewer.getDatumAxis(datumAxisId);
    }
    const datumPointId = parseDatumReferenceEntityId(entityId, DatumReferenceEntityKind.Point);
    if (datumPointId !== null) {
      return this.viewer.getDatumPoint(datumPointId);
    }
    return this.viewer.getSceneObjectById(entityId);
  }


    /**
   * 按拓扑引用优先解析基准面参考实体。
   *
   * 设计说明：
   * 1. 基准面由导入拓扑或特征拓扑创建时，拓扑引用才是权威来源；
   * 2. 即使旧代理对象仍在场景中，也不能让旧 `entityId` 抢先参与求解；
   * 3. 该函数只服务基准面控制器，不修改公共 FeatureFace/FeatureLine/FeaturePoint 读取语义。
   *
   * @param reference 基准面实体引用。
   * @returns 拓扑恢复出的参考实体；无法恢复时返回 `null`。
   */
  private resolveTopologyPriorityPlaneReference(reference: PlaneEntityReference): PlaneResolvedEntity | null {
    if (reference.assemblyTopologyRef) {
      const assemblyTopologyObject = resolveAssemblyTopologyReferenceObject(this.viewer, reference.assemblyTopologyRef);
      if (assemblyTopologyObject) {
        return assemblyTopologyObject;
      }
    }

    return this.resolveFeatureReferenceFromTopologyPick(reference);
  }


    /**
   * 根据拓扑命中快照解析当前结果体上的特征引用。
   *
   * 设计说明：
   * 1. 特征 replay 后旧代理对象 uuid 可能失效，但 `topologyPick` 保存了结果体 ID 与拓扑索引；
   * 2. 基准面重算必须解析当前结果体上的代理对象，不能继续持有旧对象；
   * 3. 这里只负责特征面、边、点三类几何引用，基准几何引用仍走原有 datum ID 解析。
   *
   * @param reference 基准面实体引用。
   * @returns 当前结果体上的特征代理对象；无法解析时返回 `null`。
   */
  private resolveFeatureReferenceFromTopologyPick(reference: PlaneEntityReference): PlaneResolvedEntity | null {
    const topologyPick = reference.topologyPick;
    if (!topologyPick) {
      return null;
    }

    // 按权威拓扑类型重新获取代理，避免特征重建后的旧 uuid 让基准面求解失效。
    return resolveModelTopologyProxyObject({
      scene: this.viewer.scene,
      topologyPick,
      getSceneObjectById: this.viewer.getSceneObjectById.bind(this.viewer),
    }) as PlaneResolvedEntity | null;
  }

  /**
   * 判断基准面定义是否依赖特征结果体几何。
   *
   * @param definition 基准面特征定义。
   * @returns 依赖特征面、曲面、线或点时返回 `true`。
   */
  private hasFeatureGeometryPlaneReference(definition: PlaneFeatureDefinition): boolean {
    return definition.refs.some((reference) => {
      return (
        reference.entityKind === ReferenceEntitySemanticType.FeatureFace
        || reference.entityKind === ReferenceEntitySemanticType.FeatureSurface
        || reference.entityKind === ReferenceEntitySemanticType.FeatureLine
        || reference.entityKind === ReferenceEntitySemanticType.FeaturePoint
        || reference.entityRef.entityType === ReferenceEntitySemanticType.FeatureFace
        || reference.entityRef.entityType === ReferenceEntitySemanticType.FeatureSurface
        || reference.entityRef.entityType === ReferenceEntitySemanticType.FeatureLine
        || reference.entityRef.entityType === ReferenceEntitySemanticType.FeaturePoint
      );
    });
  }

  /**
   * 根据基准面的计算来源重算并写回显示坐标系。
   *
   * @param datumPlane 待重算的正式基准面。
   * @returns 成功写回新坐标系时返回 `true`。
   */
  private recomputeDatumPlaneFromCalculationSource(datumPlane: DatumPlane): boolean {
    const calculationSource = datumPlane.getCalculationSource();
    if (!calculationSource) {
      return false;
    }

    const result = this.solver.solve(calculationSource.definition, calculationSource.creationContext);
    if (result.solveStatus !== PlaneSolveStatus.FullyDefined && result.solveStatus !== PlaneSolveStatus.UnderDefined) {
      return false;
    }

    const frameIndex = calculationSource.generatedPlaneCount > 1
      ? calculationSource.generatedPlaneIndex
      : calculationSource.selectedSolutionIndex;
    const frame = result.planes[Math.max(0, Math.min(result.planes.length - 1, frameIndex))];
    if (!frame) {
      return false;
    }

    // 基准面局部坐标依赖原点、法线与 X/Y 轴，重建时必须一次性写回完整 frame。
    datumPlane.updateFrame({
      originWorld: frame.origin.clone(),
      normal: frame.normal.clone(),
      xAxis: frame.xDir.clone(),
      yAxis: frame.yDir.clone(),
      width: resolveCreatedDatumPlaneDisplaySize(frame.displayWidth),
      height: resolveCreatedDatumPlaneDisplaySize(frame.displayHeight),
    });
    return true;
  }


    /**
   * 从定义中解析约束类型。
   *
   * @param definition 特征定义。
   * @returns 约束类型。
   */
  private resolveConstraintFromDefinition(definition: PlaneFeatureDefinition): PlaneConstraintType {
    const firstRef = definition.refs.find((item) => item.role === PlaneReferenceRole.First);
    const firstConstraint = firstRef?.constraint ?? PlaneConstraintType.Auto;
    if (firstConstraint !== PlaneConstraintType.Auto) {
      return firstConstraint;
    }
    const firstTwoRefs = definition.refs.filter((item) => item.role === PlaneReferenceRole.First || item.role === PlaneReferenceRole.Second);
    const hasMidPlaneTagOnFirstTwo = firstTwoRefs.some((item) => (
      item.constraint === PlaneConstraintType.MidPlane || Boolean(item.flags?.midPlane)
    ));
    if (hasMidPlaneTagOnFirstTwo) {
      return PlaneConstraintType.MidPlane;
    }
    return firstConstraint;
  }

  /**
   * 通过实体 ID 查找所在槽位。
   *
   * @param entityId 实体 ID。
   * @returns 槽位键值，未命中返回 `null`。
   */
  private findSlotByEntityId(entityId: string): PlaneSlotKey | null {
    const slots = [PlaneSlotKey.First, PlaneSlotKey.Second, PlaneSlotKey.Third];
    for (const slot of slots) {
      const candidate = this.state.refs[slot];
      if (candidate?.entityId === entityId) {
        return slot;
      }
    }
    return null;
  }

  /**
   * 压缩槽位引用，移除中间空洞。
   */
  private compactReferenceSlots(): void {
    const orderedSlots: PlaneSlotKey[] = [PlaneSlotKey.First, PlaneSlotKey.Second, PlaneSlotKey.Third];
    const selectedCandidates = orderedSlots
      .map((slot) => this.state.refs[slot])
      .filter((candidate): candidate is PlaneSelectionCandidate => Boolean(candidate));

    orderedSlots.forEach((slot, index) => {
      this.state.refs[slot] = selectedCandidates[index] ?? null;
    });
  }

  /**
   * 解析下一个激活槽位。
   *
   * @returns 下一个待填充槽位。
   */
  private resolveNextActiveSlotAfterOpen(): PlaneSlotKey {
    if (!this.state.refs[PlaneSlotKey.First]) {
      return PlaneSlotKey.First;
    }
    if (!this.state.refs[PlaneSlotKey.Second]) {
      return PlaneSlotKey.Second;
    }
    if (!this.state.refs[PlaneSlotKey.Third]) {
      return PlaneSlotKey.Third;
    }
    return PlaneSlotKey.Third;
  }

  /**
   * 为距离约束应用默认标量。
   *
   * @param previousConstraint 变更前约束。
   */
  private applyConstraintDefaultScalar(previousConstraint: PlaneConstraintType): void {
    if (this.state.constraint !== PlaneConstraintType.Distance) {
      return;
    }
    if (previousConstraint === PlaneConstraintType.Distance) {
      return;
    }
    if (Math.abs(this.state.distanceOrAngle) > 1e-9) {
      return;
    }
    this.state.distanceOrAngle = convertMmToM(10);
  }


    /**
   * 在参考引用发生变化后，收敛当前约束到合法状态。
   *
   * 设计说明：
   * 1. 普通创建态保持原有“自动推断”行为；
   * 2. 编辑特征态初始会以 `ManualLocked` 回填历史约束；
   * 3. 当用户更换/移除参考后，若旧约束已不再可用，必须自动回退到合法约束并解除锁定，
   *    否则会出现“引用变了但求解语义仍锁死在旧模式”的假重算现象。
   */
  private reconcileConstraintAfterReferenceMutation(): void {
    const previousConstraint = this.state.constraint;
    const availableConstraints = availableConstraintsForStateInternal(this.state);
    const activeEditSession = this.editSession;
    const shouldReinferFromSelectionChange = Boolean(
      activeEditSession
      && this.state.modeSource === ReferenceCommandModeSource.ManualLocked
      && previousConstraint === activeEditSession.initialConstraint
    );

    if (availableConstraints.includes(previousConstraint)) {
      if (this.state.modeSource !== ReferenceCommandModeSource.ManualLocked || shouldReinferFromSelectionChange) {
        const inferredConstraint = inferConstraintForStateInternal(this.state);
        this.state.constraint = inferredConstraint;
        // 编辑态从“偏移距离”切到新引用组合时，若新组合同时支持“角度”，优先收敛到角度。
        // 这样可以避免历史距离语义继续压制新补入参考的角度语义。
        if (
          shouldReinferFromSelectionChange
          && activeEditSession?.initialConstraint === PlaneConstraintType.Distance
          && inferredConstraint === PlaneConstraintType.Perpendicular
          && availableConstraints.includes(PlaneConstraintType.Angle)
        ) {
          this.state.constraint = PlaneConstraintType.Angle;
        }
        this.applyConstraintDefaultScalar(previousConstraint);
        if (shouldReinferFromSelectionChange) {
          this.state.modeSource = ReferenceCommandModeSource.AutoInferred;
        }
      }
      return;
    }

    const inferredConstraint = inferConstraintForStateInternal(this.state);
    this.state.constraint = availableConstraints.includes(inferredConstraint)
      ? inferredConstraint
      : (availableConstraints[0] ?? PlaneConstraintType.Auto);
    if (
      shouldReinferFromSelectionChange
      && activeEditSession?.initialConstraint === PlaneConstraintType.Distance
      && inferredConstraint === PlaneConstraintType.Perpendicular
      && availableConstraints.includes(PlaneConstraintType.Angle)
    ) {
      this.state.constraint = PlaneConstraintType.Angle;
    }
    this.applyConstraintDefaultScalar(previousConstraint);

    // 旧手动锁定约束已失效，后续引用变化应继续按自动推断推进。
    this.state.modeSource = ReferenceCommandModeSource.AutoInferred;
  }


    /**
   * 判断当前选择是否应被拒绝。
   *
   * @param candidate 待新增候选。
   * @returns 若当前组合无效则返回 `true`。
   */
  private shouldRejectSelectionCandidate(candidate: PlaneSelectionCandidate): boolean {
    if (!isPlaneLikeKind(candidate.kind)) {
      return false;
    }
    const existingSlot = this.findSlotByEntityId(candidate.entityId);
    if (existingSlot) {
      return false;
    }
    const selectedPlaneLikeCount = [PlaneSlotKey.First, PlaneSlotKey.Second, PlaneSlotKey.Third]
      .map((slot) => this.state.refs[slot])
      .filter((item): item is PlaneSelectionCandidate => Boolean(item))
      .filter((item) => isPlaneLikeKind(item.kind))
      .length;
    const selectedNonPlaneCount = [PlaneSlotKey.First, PlaneSlotKey.Second, PlaneSlotKey.Third]
      .map((slot) => this.state.refs[slot])
      .filter((item): item is PlaneSelectionCandidate => Boolean(item))
      .filter((item) => !isPlaneLikeKind(item.kind))
      .length;
    return selectedPlaneLikeCount >= 2 && selectedNonPlaneCount === 0;
  }

  /**
   * 应用无效参考组合状态。
   *
   * @param message 错误文案。
   */
  private applyInvalidSelectionState(message: string): void {
    this.clearPreview();
    this.state.preview = {
      ok: false,
      error: message,
    };
    this.state.solveStatus = PlaneSolveStatus.Inconsistent;
  }

  /**
   * 求解当前预览状态。
   *
   * @param state 命令状态。
   * @returns 预览状态。
   */
  private solvePreview(
    state: PlaneCommandState,
  ): PlanePreviewState {
    const refs = buildGeometryReferences(state);
    if (refs.length === 0) {
      return { ok: false, error: '请至少选择一个参考实体' };
    }
    if (isSingleSurfaceTangentPendingState(state)) {
      return { ok: false };
    }
    const definition = buildDefinitionInternal('__plane_preview__', state);
    // 预览与正式提交共用同一套定义构造，避免两条链路产生求解差异。
    const result: PlaneFeatureResult = this.solver.solve(definition, this.pendingCreationContext);
    return {
      ok: result.solveStatus === PlaneSolveStatus.FullyDefined || result.solveStatus === PlaneSolveStatus.UnderDefined,
      result,
      error: result.solveStatus === PlaneSolveStatus.NoSolution || result.solveStatus === PlaneSolveStatus.Inconsistent
        ? (result.diagnostics[0]?.message ?? result.statusText)
        : undefined,
    };
  }

  /**
   * 重新计算预览状态。
   */
  private recomputePreview(): void {
    if (this.state.lifecycle !== ReferenceCommandLifecycle.Open) {
      this.state.preview = { ok: false };
      this.state.solveStatus = PlaneSolveStatus.UnderDefined;
      return;
    }
    // 预览状态与渲染结果必须在同一轮求解后同步更新，避免 UI 出现短暂不一致。
    this.state.preview = this.solvePreview(this.state);
    this.state.solveStatus = this.state.preview.result?.solveStatus ?? PlaneSolveStatus.UnderDefined;
    this.handlePreviewChanged(this.state.preview);
  }
}
